import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Temporal } from 'temporal-polyfill';
import { getBusinessTimezone } from '../../utils/businessTime';
import BookingCalendar from './components/BookingCalendar/BookingCalendar';
import CalendarActionMenu from './components/CalendarActionMenu';
import { useGetRestaurantHolidays } from '../../APIs/common';
import {
  useGetWeeklyCalendarBookings,
  useGetServicePeriodColors,
  useGetMonthlyCalendarBookings,
  useGetDailyCalendarBookings
} from '../../APIs/Calendar/calendar';
import './calendar.css';

const DAY_MAP = {
  Monday: 1,
  Tuesday: 2,
  Wednesday: 3,
  Thursday: 4,
  Friday: 5,
  Saturday: 6,
  Sunday: 7
};

const MONTHS = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];

const toSafeId = (value) => {
  if (typeof value !== 'string') {
    return String(value || 'unknown')
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  return value
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
};

const toApiDate = (value) => {
  if (!value) return null;

  // ZonedDateTime → PlainDate
  if (value.toPlainDate) {
    return value.toPlainDate().toString(); // YYYY-MM-DD
  }

  // PlainDate
  return value.toString();
};

export default function Calendar() {
  const navigate = useNavigate();

  const [menuDate, setMenuDate] = useState(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedServicePeriod, setSelectedServicePeriod] = useState(null);

  const [holidays, setHolidays] = useState([]);
  const [nonWorkingDays, setNonWorkingDays] = useState([]);

  const [range, setRange] = useState(null);
  const [search, setSearch] = useState('');

  const { getRestaurantHolidays } = useGetRestaurantHolidays();

  const detectViewFromRange = (start, end) => {
    const s = start.toPlainDate ? start.toPlainDate() : start;
    const e = end.toPlainDate ? end.toPlainDate() : end;

    const days = s.until(e).days + 1;

    if (days === 1) return 'day';
    if (days <= 8) return 'week';
    return 'month';
  };

  const monthParams = useMemo(() => {
    if (!range?.start || !range?.end) return null;

    const s = range.start.toPlainDate ? range.start.toPlainDate() : range.start;
    const e = range.end.toPlainDate ? range.end.toPlainDate() : range.end;

    const mid = s.add({ days: Math.floor(s.until(e).days / 2) });

    return {
      currentMonth: MONTHS[mid.month - 1], // "january"
      year: mid.year
    };
  }, [range]);

  const view = range && range.start && range.end ? detectViewFromRange(range.start, range.end) : null;

  const {
    bookingsByService,
    isLoading: isWeeklyLoading,
    refresh: weeklyRefetch
  } = useGetWeeklyCalendarBookings(
    view === 'week' ? toApiDate(range?.start) : null,
    view === 'week' ? toApiDate(range?.end) : null,
    selectedLocation?.value,
    selectedServicePeriod?.value
  );

  const {
    bookingsByService: monthlyBookingsByService,
    isLoading: isMonthlyLoading,
    refresh: monthlyRefetch
  } = useGetMonthlyCalendarBookings(
    view === 'month' ? monthParams?.currentMonth : null,
    view === 'month' ? monthParams?.year : null,
    selectedLocation?.value,
    selectedServicePeriod?.label,
    '' // search optional
  );

  const {
    bookingsByService: dailyBookingsByService,
    isLoading: isDailyLoading,
    refresh: dailyRefetch
  } = useGetDailyCalendarBookings(view === 'day' ? toApiDate(range?.start) : null, selectedLocation?.value, selectedServicePeriod?.value);

  const { colors: serviceColors } = useGetServicePeriodColors();

  const serviceColorMap = useMemo(() => {
    const map = {};

    serviceColors.forEach((item) => {
      map[item.name.toLowerCase()] = item.color_code;
    });

    return map;
  }, [serviceColors]);

  useEffect(() => {
    const close = () => {
      setMenuDate(null);
      setMenuPosition(null);
    };
    window.addEventListener('scroll', close);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close);
      window.removeEventListener('resize', close);
    };
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (!e.detail?.value) return;
      setSelectedLocation(e.detail);
    };

    window.addEventListener('calendar-location-change', handler);
    return () => window.removeEventListener('calendar-location-change', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      // null comes from the header's Clear filters: back to every period.
      if (e.detail === null) {
        setSelectedServicePeriod(null);
        return;
      }
      if (!e.detail?.value) return;

      setSelectedServicePeriod(e.detail);
    };

    window.addEventListener('calendar-service-period-change', handler);

    return () => window.removeEventListener('calendar-service-period-change', handler);
  }, []);

  useEffect(() => {
    if (!selectedLocation?.value) return;

    (async () => {
      const res = await getRestaurantHolidays(selectedLocation.value);

      setHolidays(res?.holidays || []);
      setNonWorkingDays(res?.nonWorkingDays || []);
    })();
  }, [selectedLocation]);

  // useEffect(() => {
  //   const handler = () => {
  //     if (view === 'week') {
  //       weeklyRefetch();
  //     } else if (view === 'month') {
  //       monthlyRefetch();
  //     }
  //   };

  //   window.addEventListener('calendar-booking-added', handler);
  //   return () => window.removeEventListener('calendar-booking-added', handler);
  // }, [view, weeklyRefetch, monthlyRefetch]);

  useEffect(() => {
    const handler = () => {
      if (view === 'week') {
        weeklyRefetch();
      } else if (view === 'month') {
        monthlyRefetch();
      } else if (view === 'day') {
        dailyRefetch();
      }
    };

    window.addEventListener('calendar-booking-added', handler);
    return () => window.removeEventListener('calendar-booking-added', handler);
  }, [view, weeklyRefetch, monthlyRefetch, dailyRefetch]);

  const holidayEvents = useMemo(
    () =>
      holidays.map((h) => ({
        id: `holiday-${h.id}`,
        title: h.holiday_name || 'Holiday',
        start: Temporal.PlainDate.from(h.date),
        end: Temporal.PlainDate.from(h.date)
      })),
    [holidays]
  );

  const nonWorkingDayEvents = useMemo(() => {
    if (!nonWorkingDays.length || !range?.start || !range?.end) return [];

    const startDate = range.start instanceof Temporal.ZonedDateTime ? range.start.toPlainDate() : range.start;

    const endDate = range.end instanceof Temporal.ZonedDateTime ? range.end.toPlainDate() : range.end;

    const dayIndexes = nonWorkingDays.map((d) => DAY_MAP[d]).filter(Boolean);

    const events = [];

    for (let d = startDate; Temporal.PlainDate.compare(d, endDate) <= 0; d = d.add({ days: 1 })) {
      if (dayIndexes.includes(d.dayOfWeek)) {
        events.push({
          id: `closed-${d.toString()}`,
          title: 'Closed',
          start: d,
          end: d,
          allDay: true,
          data: { type: 'closed' }
        });
      }
    }

    return events;
  }, [nonWorkingDays, range]);

  // const weeklyBookingEvents = useMemo(() => {
  //   if (!bookingsByService || view !== 'week') return [];

  //   const all = [];

  //   Object.entries(bookingsByService).forEach(([serviceName, bookings]) => {
  //     bookings.forEach((b) => {
  //       all.push({ serviceName, b });
  //     });
  //   });

  //   // group by date
  //   const groupedByDate = {};

  //   all.forEach(({ serviceName, b }) => {
  //     const date = b.date;
  //     if (!groupedByDate[date]) groupedByDate[date] = [];
  //     groupedByDate[date].push({ serviceName, b });
  //   });

  //   const events = [];

  //   Object.entries(groupedByDate).forEach(([date, list]) => {
  //     list.forEach(({ serviceName, b }, index) => {
  //       const start = Temporal.ZonedDateTime.from(`${b.date}T${b.time}:00[UTC]`);
  //       const end = start.add({ minutes: b?.location?.slot_interval || 60 });

  //       events.push({
  //         id: `booking-${b.id}`,
  //         title: `${b.customer.name}`,
  //         start,
  //         end,
  //         data: {
  //           type: 'booking',
  //           bookingId: b.booking_no,
  //           customerName: b.customer.name,
  //           phone: b.customer.phone,
  //           seats: b.no_of_guests,
  //           service: serviceName,
  //           color: serviceColorMap[(serviceName || '').toLowerCase()] || '#9CA3AF',
  //           raw: b,
  //           date,
  //           dayIndex: index,
  //           dayCount: list.length
  //         }
  //       });
  //     });
  //   });

  //   return events;
  // }, [bookingsByService, view, serviceColorMap]);

  const sourceBookingsByService = view === 'day' ? dailyBookingsByService : view === 'week' ? bookingsByService : null;
  const weeklyBookingEvents = useMemo(() => {
    if (!sourceBookingsByService || (view !== 'week' && view !== 'day')) return [];

    const all = [];

    Object.entries(sourceBookingsByService).forEach(([serviceName, bookings]) => {
      bookings.forEach((b) => {
        all.push({ serviceName, b });
      });
    });

    const groupedByDate = {};

    all.forEach(({ serviceName, b }) => {
      const date = b.date;
      if (!groupedByDate[date]) groupedByDate[date] = [];
      groupedByDate[date].push({ serviceName, b });
    });

    const events = [];

    Object.entries(groupedByDate).forEach(([date, list]) => {
      list.forEach(({ serviceName, b }, index) => {
        // Booking times are business wall-clock times: build them in the same
        // zone the calendar shows (BookingCalendar's timezone), so they display
        // exactly as booked.
        const start = Temporal.ZonedDateTime.from(`${b.date}T${b.time}:00[${getBusinessTimezone()}]`);
        const end = start.add({ minutes: b?.location?.slot_interval || 60 });

        events.push({
          id: `booking-${b.id}`,
          title: `${b.customer.name}`,
          start,
          end,
          data: {
            type: 'booking',
            bookingId: b.booking_no,
            customerName: b.customer.name,
            phone: b.customer.phone,
            seats: b.no_of_guests,
            service: serviceName,
            color: serviceColorMap[(serviceName || '').toLowerCase()] || '#9CA3AF',
            status: b.booking_status,
            raw: b,
            date,
            dayIndex: index,
            dayCount: list.length
          }
        });
      });
    });

    return events;
  }, [sourceBookingsByService, view, serviceColorMap]);

  const monthSourceBookings = view === 'month' ? monthlyBookingsByService : bookingsByService;

  const monthServiceEvents = useMemo(() => {
    if (!monthSourceBookings || view !== 'month') return [];

    const dateServiceMap = {};

    Object.entries(monthSourceBookings).forEach(([serviceName, bookings]) => {
      if (!Array.isArray(bookings) || bookings.length === 0) return;

      bookings.forEach((booking) => {
        const date = booking.date;

        if (!date) return;

        // init date
        if (!dateServiceMap[date]) {
          dateServiceMap[date] = {};
        }

        // init service for date
        if (!dateServiceMap[date][serviceName]) {
          dateServiceMap[date][serviceName] = {
            count: 0,
            bookings: []
          };
        }

        // increment
        dateServiceMap[date][serviceName].count += 1;

        // store booking row
        dateServiceMap[date][serviceName].bookings.push({
          customer: booking.customer,
          time: booking.time_formatted,
          bookingNo: booking?.id,
          color: booking?.service_period?.color_code,
          date: booking.date,
          status: booking.booking_status
        });
      });
    });

    const events = [];

    Object.entries(dateServiceMap).forEach(([date, services]) => {
      Object.entries(services).forEach(([serviceName, info]) => {
        const safeService = toSafeId(serviceName);

        events.push({
          id: `month-${date}-${safeService}`,
          title: `${serviceName} – ${info.count}`,
          start: Temporal.PlainDate.from(date),
          end: Temporal.PlainDate.from(date),
          allDay: true,
          data: {
            service: serviceName,
            count: info.count,
            bookings: info.bookings,
            color: serviceColorMap[serviceName.toLowerCase()] || '#9CA3AF'
          }
        });
      });
    });

    return events;
  }, [monthSourceBookings, view, serviceColorMap]);

  const events = useMemo(() => {
    //remove comment when api comes
    if (!selectedLocation?.value) return [];

    if (view === 'month') {
      return [...monthServiceEvents, ...holidayEvents, ...nonWorkingDayEvents];
    }

    return [...weeklyBookingEvents, ...holidayEvents, ...nonWorkingDayEvents];
  }, [selectedLocation, view, weeklyBookingEvents, monthServiceEvents, holidayEvents, nonWorkingDayEvents]);

  useEffect(() => {
    const dayEls = document.querySelectorAll('.sx__time-grid-day');

    dayEls.forEach((el) => {
      el.classList.remove('sx__day--closed');
    });

    const closedDates = new Set(events.filter((e) => e.data?.type === 'closed').map((e) => e.start.toString()));

    dayEls.forEach((el) => {
      const date = el.getAttribute('data-time-grid-date');
      if (closedDates.has(date)) {
        el.classList.add('sx__day--closed');
      }
    });
  }, [events]);

  return (
    <>
      <BookingCalendar
        events={events}
        onDateCellClick={(value, e) => {
          const date = value instanceof Temporal.ZonedDateTime ? value.toPlainDate() : value;

          setMenuDate(date);
          setMenuPosition({
            x: Math.min(e.clientX, window.innerWidth - 220),
            y: Math.min(e.clientY, window.innerHeight - 120)
          });
        }}
        onRangeChange={({ start, end }) => {
          setRange({ start, end });
        }}
        view={view}
        dailyRefetch={dailyRefetch}
        onSearchChange={setSearch}
      />

      {/* {menuDate && menuPosition && (
        <CalendarActionMenu
          position={menuPosition}
          onAddBooking={() => {
            navigate('/booking-management/new-booking', {
              state: { date: menuDate.toString() }
            });
            setMenuDate(null);
            setMenuPosition(null);
          }}
          onAssignStaff={() => {
            navigate('/staff/assign', {
              state: { date: menuDate.toString() }
            });
            setMenuDate(null);
            setMenuPosition(null);
          }}
          onClose={() => {
            setMenuDate(null);
            setMenuPosition(null);
          }}
        />
      )} */}
    </>
  );
}
